<template>
  <component
    :is="tag"
    :class="
      useDefaultStyles
        ? [
            'custom-btn',
            `btn-${variant}`,
            `size-${size}`,
            { active: active },
            { disabled: disabled },
            { 'icon-left': iconPosition === 'left' },
            { 'icon-right': iconPosition === 'right' }
          ]
        : []
    "
    v-bind="$attrs"
    @touchend="handleClick"
    @click="handleClick"
    :disabled="disabled"
  >
    <font-awesome-icon v-if="icon && iconPosition === 'left'" :icon="icon" class="icon" />
    <slot v-if="!title" />
    <span v-else>{{ title }}</span>
    <font-awesome-icon v-if="icon && iconPosition === 'right'" :icon="icon" class="icon" />
    <font-awesome-icon v-if="icon && iconPosition === 'center'" :icon="icon" class="icon" />
  </component>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import type { ManagedTimeout } from '@/customTypes'
import { useTimeout } from '@/utils/helpers'

interface Props {
  title?: string
  tag?: string
  variant?:
    | 'primary'
    | 'secondary'
    | 'success'
    | 'info'
    | 'warning'
    | 'danger'
    | 'light'
    | 'dark'
    | 'black'
    | 'white'
    | 'grey'
  icon?: object
  iconPosition?: 'left' | 'right' | 'center'
  active?: boolean
  disabled?: boolean
  size?: 'sm' | 'md' | 'lg'
  link?: string
  target?: string
  useDefaultStyles?: boolean
  onClick?: () => void
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  tag: 'button',
  variant: 'primary',
  icon: undefined,
  iconPosition: 'right',
  active: false,
  disabled: false,
  size: 'md',
  link: '',
  target: '_self',
  onClick: () => {},
  useDefaultStyles: true
})

const debounceDelay = 200
const { setManagedTimeout, clearManagedTimeout } = useTimeout()

const emit = defineEmits<{
  (e: 'click'): void
}>()

const isDebouncing = ref(false)

const debounce = (func: Function, delay: number) => {
  const timeoutId = ref<ManagedTimeout | null>(null)
  return (...args: any[]) => {
    clearManagedTimeout(timeoutId)
    timeoutId.value = setManagedTimeout(() => func(...args), delay)
  }
}

const debouncedClick = debounce(() => {
  isDebouncing.value = false
  props.onClick()
}, debounceDelay)

const handleClick = () => {
  if (props.disabled || isDebouncing.value) return

  isDebouncing.value = true

  if (props.link) {
    window.open(props.link, props.target)
  } else {
    debouncedClick()
  }
}
</script>

<style lang="scss" scoped>
$button-colors: (
  'primary': $primary,
  'secondary': $secondary,
  'success': $success,
  'info': $info,
  'warning': $warning,
  'danger': $danger,
  'light': $light,
  'dark': $dark,
  'black': $black,
  'grey': $grey
);

.custom-btn {
  font-weight: 600;
  border-radius: 0.5rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  font-weight: 600;
  border-radius: 0.5rem;

  &.size-sm {
    padding: 0.25rem 0.5rem;
    font-size: 0.875rem;
  }

  &.size-md {
    padding: 0.7rem 0.9rem;
    font-size: 1rem;
  }

  &.size-lg {
    padding: 0.85rem 1rem;
    font-size: 1.25rem;
  }

  &.disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &.icon-left .icon {
    padding-right: 0.75rem;
    padding-left: 0;
  }

  &.icon-right .icon {
    padding-left: 0.75rem;
    padding-right: 0;
  }
}

@each $name, $color in $button-colors {
  .btn-#{$name} {
    border-color: $color;
    background-color: $color;
    color: $white;

    &:hover:not(.disabled) {
      background-color: darken($color, 5%);
    }

    &.active {
      background-color: $white;
      color: $color;

      &:hover:not(.disabled) {
        background-color: $white;
        color: darken($color, 10%);
        border-color: darken($color, 10%);
      }
    }
  }
}

.btn-white {
  background-color: $black;
  color: $white;
  border-color: $white;

  &:hover:not(.disabled) {
    background-color: $white;
    color: $black;
  }

  &.active {
    background-color: $white;
    color: $black;
  }
}
</style>
